/**
 * Carrying the score from one report screen into the next.
 *
 * The reveal shows the score large in the middle of the frame, and the screen
 * after it shows the same score small at the top. Rather than fading one out
 * and the other in, the second screen starts each part of its score exactly
 * where the reveal left that part and moves it home, so the learner sees one
 * number travel rather than two numbers swap.
 *
 * Both screens mark the parts of their score with this attribute, the report
 * measures the reveal's on the way out, and the next screen animates its own
 * from those positions.
 */
export const SCORE_PART_ATTRIBUTE = "data-score-part";

export type ScorePart = "eyebrow" | "score" | "separator" | "out-of";

export interface ScorePartRect {
  left: number;
  top: number;
  height: number;
}

export type ScorePartRects = Partial<Record<ScorePart, ScorePartRect>>;

/** How long the score takes to travel, and how it eases into place. */
export const SCORE_CARRY_MS = 700;
export const SCORE_CARRY_EASING = "cubic-bezier(0.16, 0, 0.3, 1)";

/** Where each marked part of the score inside `root` is on screen now. */
export const measureScoreParts = (root: Element): ScorePartRects => {
  const rects: ScorePartRects = {};
  root
    .querySelectorAll<HTMLElement>(`[${SCORE_PART_ATTRIBUTE}]`)
    .forEach((element) => {
      const part = element.getAttribute(SCORE_PART_ATTRIBUTE) as ScorePart;
      const { left, top, height } = element.getBoundingClientRect();
      rects[part] = { left, top, height };
    });
  return rects;
};

/**
 * Moves each marked part of the score inside `root` from where it was in
 * `from` to where it is now. A part either screen lacks, or one with no size
 * to scale against, is left where it is rather than guessed at.
 */
export const carryScoreParts = (root: Element, from: ScorePartRects): void => {
  root
    .querySelectorAll<HTMLElement>(`[${SCORE_PART_ATTRIBUTE}]`)
    .forEach((element) => {
      const part = element.getAttribute(SCORE_PART_ATTRIBUTE) as ScorePart;
      const start = from[part];
      const end = element.getBoundingClientRect();
      if (!start || end.height === 0 || typeof element.animate !== "function")
        return;

      // Scaled about the top-left corner, so the corner is what the
      // translation moves and the two compose without correcting for size.
      const dx = start.left - end.left;
      const dy = start.top - end.top;
      const scale = start.height / end.height;
      element.animate(
        [
          {
            transformOrigin: "0 0",
            transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
          },
          { transformOrigin: "0 0", transform: "none" },
        ],
        { duration: SCORE_CARRY_MS, easing: SCORE_CARRY_EASING }
      );
    });
};
